<template>
  <!-- 예약 시간 변경 창(같은 날). 휴대폰은 아래에서 올라오는 시트, 넓은 화면은 위쪽에 놓이는 창. -->
  <component
    :is="useNativeDialog ? 'dialog' : 'section'"
    ref="dialog"
    class="time-change"
    :class="{ 'time-change--inline': !useNativeDialog, 'time-change--closing': closing }"
    :lang="domLanguage"
    aria-labelledby="time-change-title"
    @cancel.prevent="close"
    @close="finish"
    @click="onDialogClick"
    @keydown.esc="onEscape"
  >
    <div class="time-change__panel">
      <header class="time-change__header">
        <h3 id="time-change-title" ref="title" class="time-change__title font-danjunghae" tabindex="-1">
          {{ $t("page.mypage.reserv.time-change.title") }}
        </h3>
        <button type="button" class="time-change__close" :aria-label="$t('button.close')" @click="close">
          <v-icon size="22" aria-hidden="true">mdi-close</v-icon>
        </button>
      </header>

      <!-- 바꾼 뒤 -->
      <div v-if="phase === 'done'" class="time-change__done" role="status">
        <img class="time-change__castle" src="@/assets/deco/line_castle.png" alt="" aria-hidden="true" />
        <p class="time-change__done-title">{{ $t("page.mypage.reserv.time-change.done-title") }}</p>
        <p class="time-change__done-when">
          <span>{{ dateLabel }}</span>
          <strong>{{ result.time }}</strong>
        </p>
        <p v-if="result.maker" class="time-change__maker-fixed">
          <span>{{ $t("page.mypage.reserv.time-change.maker-title") }}</span>
          <strong>{{ staffName({ name: result.maker }) }}</strong>
        </p>
        <p v-if="isMakershot" class="time-change__maker-fixed">
          <span>{{ shootLabel }}</span>
          <strong>{{ shootTime(result.time) }}</strong>
        </p>
        <p v-if="result.noticeSent" class="time-change__done-note">{{ $t("page.mypage.reserv.time-change.done-notice") }}</p>
        <!-- 더 바꿀 수 없으면 '남은 횟수 0번' 대신 규칙 한 줄을 보인다 -->
        <p v-if="resultLeft === 0" class="time-change__done-note time-change__done-limit">
          <span v-for="line in sentences($t('page.mypage.reserv.time-change.blocked-limit'))" :key="line">{{ line }}</span>
        </p>
        <p v-else-if="resultLeft !== null" class="time-change__left">{{ $t("page.mypage.reserv.time-change.left", { count: resultLeft }) }}</p>
        <button type="button" class="time-change__primary" @click="close">{{ $t("button.close") }}</button>
      </div>

      <template v-else>
        <div ref="body" class="time-change__body">
          <dl class="time-change__facts">
            <div>
              <dt>{{ $t("page.mypage.reserv.time-change.date-label") }}</dt>
              <dd>{{ dateLabel }}</dd>
            </div>
            <div>
              <dt>{{ isMakershot ? visitLabel : $t("page.mypage.reserv.time-change.current-label") }}</dt>
              <dd>{{ currentTime }}</dd>
            </div>
            <div v-if="isMakershot">
              <dt>{{ shootLabel }}</dt>
              <dd>{{ shootTime(currentTime) }}</dd>
            </div>
          </dl>

          <!-- 불러오는 중: 시간 칸·메이커 자리·아래 단추 칸을 불러온 뒤와 같은 자리에 잡아 둔다
               (안내 한 줄은 자리만 잡고 보이지 않는다). 휴대폰 시트가 불러온 직후 크게 커지지 않게 하려는 것이다. -->
          <template v-if="phase === 'loading'">
            <div class="time-change__group" role="status" aria-live="polite">
              <p class="time-change__label" aria-hidden="true">{{ $t("page.mypage.reserv.time-change.pick-title") }}</p>
              <span class="time-change__sr">{{ $t("page.mypage.reserv.time-change.loading") }}</span>
              <p class="time-change__rule time-change__rule--space" aria-hidden="true"><span v-for="line in sentences($t('page.mypage.reserv.time-change.rule'))" :key="line">{{ line }}</span></p>
              <div class="time-change__chips" aria-hidden="true">
                <span v-for="n in 8" :key="n" class="time-change__chip time-change__chip--skeleton"></span>
              </div>
            </div>
            <div v-if="currentMaker" class="time-change__maker-area" aria-hidden="true">
              <p class="time-change__maker-fixed time-change__maker-now">
                <span>{{ $t("page.mypage.reserv.time-change.maker-title") }}</span>
                <strong>{{ currentMaker }}</strong>
              </p>
              <div class="time-change__maker-stack time-change__maker-stack--loading"></div>
            </div>
          </template>

          <!-- 문장마다 줄을 나눠, 문장 끝 한두 낱말만 다음 줄로 넘어가지 않게 한다(글자는 그대로) -->
          <div v-else-if="phase === 'failed'" class="time-change__message" role="alert">
            <p><span v-for="line in sentences($t('page.mypage.reserv.time-change.load-failed'))" :key="line">{{ line }}</span></p>
            <!-- 손님이 할 수 있는 일이 이것뿐이라 주 단추로 둔다 -->
            <button type="button" class="time-change__primary" @click="load">{{ $t("page.mypage.reserv.reload-button") }}</button>
          </div>

          <p v-else-if="phase === 'blocked'" class="time-change__message" role="status">
            <span v-for="line in sentences(blockedMessage)" :key="line">{{ line }}</span>
          </p>

          <p v-else-if="!slots.length" class="time-change__message" role="status">{{ $t("page.mypage.reserv.time-change.empty") }}</p>

          <template v-else>
            <fieldset class="time-change__group">
              <legend class="time-change__label">{{ $t("page.mypage.reserv.time-change.pick-title") }}</legend>
              <p class="time-change__rule"><span v-for="line in sentences($t('page.mypage.reserv.time-change.rule'))" :key="line">{{ line }}</span></p>
              <div class="time-change__chips">
                <label v-for="slot in slots" :key="slot.time" class="time-change__chip" :class="{ 'is-selected': selectedTime === slot.time }">
                  <input v-model="selectedTime" class="time-change__radio" type="radio" name="time-change-time" :value="slot.time" :disabled="submitting" />
                  <span>{{ slot.time }}</span>
                </label>
              </div>
            </fieldset>

            <!-- 프리미엄: 지금 담당 메이커와, 고른 시간에 누가 진행하는지. 메이커 이야기는 이 칸 한곳에 모은다.
                 나올 수 있는 모양을 모두 같은 자리에 겹쳐 두고 지금 것만 보여, 시간을 누를 때 창 높이가 바뀌지 않는다
                 (나머지는 자리만 잡는다). -->
            <div v-if="makerStates.length" ref="makerArea" class="time-change__maker-area">
              <p v-if="currentMaker" class="time-change__maker-fixed time-change__maker-now">
                <span>{{ $t("page.mypage.reserv.time-change.maker-title") }}</span>
                <strong>{{ currentMaker }}</strong>
              </p>
              <div class="time-change__maker-stack">
              <div
                v-for="state in makerStates"
                :key="state.key"
                class="time-change__maker-state"
                :class="{ 'is-sizer': state.sizer, 'is-hint': !state.slot }"
                :aria-hidden="state.sizer ? 'true' : null"
              >
                <p v-if="!state.slot" class="time-change__maker-hint">{{ $t("page.mypage.reserv.time-change.maker-hint") }}</p>
                <template v-else>
                  <p v-if="changesMaker(state.slot)" class="time-change__maker-note" :role="state.sizer ? null : 'status'">
                    {{ $t("page.mypage.reserv.time-change.maker-changed", { time: state.slot.time }) }}
                  </p>
                  <fieldset v-if="state.slot.staff.length > 1" class="time-change__group time-change__group--makers">
                    <legend class="time-change__label">{{ $t("page.mypage.reserv.time-change.maker-pick-title") }}</legend>
                    <div class="time-change__makers">
                      <label
                        v-for="staff in state.slot.staff"
                        :key="staff.id"
                        class="time-change__maker"
                        :class="{ 'is-selected': !state.sizer && selectedStaffId === staff.id }"
                      >
                        <input
                          class="time-change__radio"
                          type="radio"
                          :name="state.sizer ? null : 'time-change-maker'"
                          :value="staff.id"
                          :checked="!state.sizer && selectedStaffId === staff.id"
                          :disabled="state.sizer || submitting"
                          @change="selectedStaffId = staff.id"
                        />
                        <span>
                          {{ staffName(staff) }}
                          <small v-if="staff.current" class="time-change__maker-tag">{{ $t("page.mypage.reserv.time-change.maker-current-tag") }}</small>
                        </span>
                      </label>
                    </div>
                  </fieldset>
                  <p v-else-if="changesMaker(state.slot)" class="time-change__maker-fixed">
                    <span>{{ $t("page.mypage.reserv.time-change.maker-new-title") }}</span>
                    <strong>{{ staffName(state.slot.staff[0]) }}</strong>
                  </p>
                  <p v-else class="time-change__maker-fixed" :role="state.sizer ? null : 'status'">
                    <span>{{ $t("page.mypage.reserv.time-change.maker-same-title", { time: state.slot.time }) }}</span>
                    <strong>{{ staffName(state.slot.staff[0]) }}</strong>
                  </p>
                </template>
              </div>
              </div>
            </div>
          </template>
        </div>

        <footer v-if="phase === 'loading' || (phase === 'ready' && slots.length)" class="time-change__footer">
          <!-- 오류 글은 다음에 '이 시간으로 바꾸기'를 누를 때까지 그대로 둔다. 다른 시간을 고르는 순간 지우면
               아래 칸 높이가 줄어, 방금 누른 시간 칸이 손가락 밑에서 움직인다. -->
          <p v-if="errorMessage" class="time-change__error" role="alert">
            <v-icon size="18" aria-hidden="true">mdi-alert-circle-outline</v-icon>
            <span>{{ errorMessage }}</span>
          </p>
          <div class="time-change__status">
            <p class="time-change__summary" aria-live="polite">
              <template v-if="selectedTime">
                <span class="time-change__sr">{{ $t("page.mypage.reserv.time-change.summary-sr", { from: currentTime, to: selectedTime }) }}</span>
                <span aria-hidden="true">{{ currentTime }}</span>
                <v-icon size="16" aria-hidden="true">mdi-arrow-right</v-icon>
                <strong aria-hidden="true">{{ selectedTime }}</strong>
              </template>
              <template v-else>{{ $t("page.mypage.reserv.time-change.pick-guide") }}</template>
            </p>
            <!-- 남은 횟수는 누르는 순간에도 보이게 단추 바로 위에 둔다(불러오기 전에는 자리만 잡는다) -->
            <p class="time-change__left" :class="{ 'is-waiting': !rule }">{{ $t("page.mypage.reserv.time-change.left", { count: remainingChanges }) }}</p>
          </div>
          <!-- 메이커샷: 고른 방문 시간에 맞춰 바뀌는 촬영 시간. 고르기 전에는 자리만 잡아 둔다. -->
          <p v-if="isMakershot" class="time-change__shoot" :class="{ 'is-waiting': !selectedTime }">
            <span>{{ shootLabel }}</span>
            <strong>{{ shootTime(selectedTime || currentTime) }}</strong>
          </p>
          <!-- 보내는 동안에도 단추 색은 그대로 두고 '바꾸는 중'만 알린다. 꺼진 색은 시간을 안 골랐을 때만 쓴다. -->
          <button
            type="button"
            class="time-change__primary"
            :class="{ 'is-busy': submitting }"
            :disabled="!selectedTime || phase === 'loading'"
            :aria-disabled="submitting ? 'true' : null"
            @click="submit"
          >
            <span v-if="submitting" class="time-change__spinner" aria-hidden="true"></span>
            {{ submitting ? $t("page.mypage.reserv.time-change.submitting") : $t("page.mypage.reserv.time-change.submit") }}
          </button>
        </footer>

        <!-- 바꿀 수 없을 때: 읽고 나서 엄지로 누를 수 있게 아래에 단추를 둔다 -->
        <footer v-else-if="phase === 'blocked' || phase === 'ready'" class="time-change__footer">
          <a v-if="canAskStore" class="time-change__secondary time-change__dm" :href="dmUrl" target="_blank" rel="noopener noreferrer">
            <v-icon size="18" aria-hidden="true">mdi-instagram</v-icon>
            {{ $t("page.mypage.reserv.time-change.dm-button") }}
          </a>
          <button type="button" class="time-change__secondary" @click="close">{{ $t("button.close") }}</button>
        </footer>
      </template>
    </div>
  </component>
</template>

<script>
import { mapState } from "vuex"
import { INSTAGRAM_DM_URL } from "@/constant"
import { domLang, localizedText } from "@/util/lang"

const CLOSE_MS = 160
// 통신이 끊겨도 창에 갇히지 않게, 이 창의 요청은 이 시간 안에 끝낸다.
const REQUEST_TIMEOUT_MS = 15000
const DATE_LOCALES = { ko: "ko-KR", en: "en-US", jp: "ja-JP" }
// 서버가 알려 준 까닭 → 보여 줄 글. 여기에 없는 까닭은 '잠시 뒤 다시, 안 되면 인스타그램 DM 문의'(blocked-other)로 안내한다.
const BLOCKED_KEYS = {
  WINDOW_CLOSED: "blocked-window",
  LIMIT_REACHED: "blocked-limit",
  NOT_PAID: "blocked-ended",
  PASSED: "blocked-ended",
}
// 문의해도 달라질 것이 없는 까닭(이미 지났거나 취소한 예약)에는 문의 단추를 두지 않는다.
// 문의 단추를 두지 않는 까닭: 결제 전·지난 예약, 그리고 횟수를 다 쓴 예약(회장 10/9: 말하면 또 바꿔 준다고 읽히면 안 됨)
const NO_ASK_REASONS = ["NOT_PAID", "PASSED", "LIMIT_REACHED"]
// 다른 손님이 그 시간을 먼저 잡았을 때 서버가 주는 코드
const SLOT_TAKEN_CODES = ["BOOKING_SLOT_BUSY", "TIME_CHANGE_SLOT_TAKEN"]

const timeOnly = (value) => String(value || "").split(",")[0].slice(0, 5)

export default {
  name: "ReservationTimeChange",

  props: {
    // 마이페이지 예약 목록의 한 줄(reservation_id, year, month, day, times)
    reservation: { type: Object, default: null },
    // 메이커샷 예약이면 방문 시간에서 촬영 시간까지의 분(예약 상세의 '메이커샷 촬영 시간'과 같은 셈). 프리미엄은 null.
    shootOffset: { type: Number, default: null },
  },

  data: () => ({
    useNativeDialog: typeof window !== "undefined" && typeof window.HTMLDialogElement === "function" && typeof window.HTMLDialogElement.prototype.showModal === "function",
    phase: "loading", // loading | ready | blocked | failed | done
    rule: null,
    slots: [],
    // 서버가 알려 준 지금 예약(시간·메이커). 응답을 못 받은 사이에 바뀌었을 수 있어 목록 값보다 먼저 쓴다.
    server: null,
    selectedTime: "",
    selectedStaffId: null,
    submitting: false,
    errorMessage: "",
    result: null,
    closing: false,
    finished: false,
  }),

  computed: {
    ...mapState(["lang"]),
    domLanguage() {
      return domLang(this.lang)
    },
    dmUrl() {
      return INSTAGRAM_DM_URL
    },
    isMakershot() {
      return this.shootOffset !== null
    },
    // 예약 상세가 쓰는 이름을 그대로 쓴다(끝의 ' : '만 뗀다)
    visitLabel() {
      return this.$t("page.mypage.reserv.visit-time").replace(/\s*:\s*$/, "")
    },
    shootLabel() {
      return this.$t("page.mypage.reserv.shooting-time").replace(/\s*:\s*$/, "")
    },
    currentTime() {
      return timeOnly((this.server && this.server.time) || (this.reservation && this.reservation.times))
    },
    dateLabel() {
      if (!this.reservation) return ""
      const { year, month, day } = this.reservation
      const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)))
      return new Intl.DateTimeFormat(DATE_LOCALES[this.lang] || DATE_LOCALES.ko, { month: "long", day: "numeric", weekday: "short", timeZone: "UTC" }).format(date)
    },
    selectedSlot() {
      return this.slots.find((slot) => slot.time === this.selectedTime) || null
    },
    // 지금 담당 메이커 이름(메이커는 프리미엄 예약에만 있다)
    currentMaker() {
      return this.staffName({ name: (this.server && this.server.maker) || (this.reservation && this.reservation.maker) })
    },
    // 메이커 칸에 겹쳐 둘 모양들: 맨 앞이 지금 보이는 것, 나머지는 높이만 잡는다.
    // 같은 높이가 나오는 시간(바뀜 여부와 메이커 구성이 같은 시간)은 하나만 둔다.
    makerStates() {
      if (!this.slots.some((slot) => slot.staff && slot.staff.length)) return []
      const states = [
        { key: "live", slot: this.selectedSlot, sizer: false },
        { key: "size-hint", slot: null, sizer: true },
      ]
      const seen = {}
      this.slots.forEach((slot) => {
        const key = `${this.changesMaker(slot) ? "c" : "s"}:${(slot.staff || []).map((item) => item.id).join(",")}`
        if (seen[key]) return
        seen[key] = true
        states.push({ key: `size-${key}`, slot, sizer: true })
      })
      return states
    },
    remainingChanges() {
      if (!this.rule) return 0
      return Math.max(0, Number(this.rule.maxChanges) - Number(this.rule.usedChanges))
    },
    // 바꾼 뒤 남은 횟수(서버가 알려 준 값이 있을 때만)
    resultLeft() {
      const left = this.result ? Number(this.result.maxChanges) - Number(this.result.usedChanges) : NaN
      return Number.isFinite(left) ? Math.max(0, left) : null
    },
    blockedMessage() {
      const key = BLOCKED_KEYS[this.rule && this.rule.reason] || "blocked-other"
      return this.$t(`page.mypage.reserv.time-change.${key}`)
    },
    canAskStore() {
      return this.phase === "blocked" && !NO_ASK_REASONS.includes(this.rule && this.rule.reason)
    },
  },

  watch: {
    // 시간을 고르면 그 시간의 메이커를 맞춘다. 지금 메이커가 되면 그대로, 아니면 첫 번째.
    selectedTime() {
      const staff = this.selectedSlot ? this.selectedSlot.staff : []
      const keep = staff.find((item) => item.current)
      this.selectedStaffId = staff.length ? (keep || staff[0]).id : null
      this.$nextTick(this.revealMakerArea)
    },
  },

  mounted() {
    if (this.useNativeDialog) {
      this.$refs.dialog.showModal()
    }
    this.$nextTick(() => this.$refs.title && this.$refs.title.focus())
    this.load()
  },

  beforeDestroy() {
    if (this.useNativeDialog && this.$refs.dialog && this.$refs.dialog.open) {
      this.finished = true
      this.$refs.dialog.close()
    }
  },

  methods: {
    staffName(staff) {
      return localizedText(staff && staff.name, this.lang)
    },

    // 방문 시간에 촬영까지의 분을 더한 시각(메이커샷)
    shootTime(time) {
      const [hours, minutes] = String(time || "").split(":").map(Number)
      if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return ""
      const total = hours * 60 + minutes + Number(this.shootOffset || 0)
      return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`
    },

    // 글을 문장 단위로 나눈다(화면에서 문장마다 줄을 바꾸는 데 쓴다)
    sentences(text) {
      // 가운뎃점(취소·환불) 앞뒤에서는 줄이 갈리지 않게 낱말 이음표를 넣는다
      return (String(text || "").match(/[^.!?。]+[.!?。]?/g) || []).map((line) => line.trim().replace(/\s*·\s*/g, "\u2060·\u2060")).filter(Boolean)
    },

    // 이 시간으로 옮기면 메이커가 바뀌는가: 그 시간에 지금 메이커가 안 될 때
    changesMaker(slot) {
      const staff = (slot && slot.staff) || []
      return staff.length > 0 && !staff.some((item) => item.current)
    },

    // 메이커가 바뀌거나 골라야 하는데 그 칸이 가려져 있으면 보이는 자리까지만 굴린다.
    revealMakerArea() {
      const body = this.$refs.body
      const area = this.$refs.makerArea
      const slot = this.selectedSlot
      if (!body || !area || !slot || !(this.changesMaker(slot) || (slot.staff || []).length > 1)) return
      // 자리만 잡아 둔 빈 곳이 아니라, 지금 보이는 글·단추의 아래 끝이 가려졌을 때 그만큼만 굴린다.
      const live = area.querySelector(".time-change__maker-state:not(.is-sizer)")
      const last = live && live.lastElementChild
      if (!last) return
      const hidden = last.getBoundingClientRect().bottom - body.getBoundingClientRect().bottom
      if (hidden <= 1) return
      const top = Math.round(body.scrollTop + hidden + 12)
      const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches
      if (!reduce && typeof body.scrollTo === "function") body.scrollTo({ top, behavior: "smooth" })
      else body.scrollTop = top
    },

    async load() {
      this.phase = "loading"
      this.errorMessage = ""
      try {
        const { data } = await this.$http.get(`/api/booking/reservations/${this.reservation.reservation_id}/time-options`, { timeout: REQUEST_TIMEOUT_MS })
        this.rule = data.rule
        this.slots = Array.isArray(data.slots) ? data.slots : []
        this.server = data.reservation || null
        if (!this.slots.some((slot) => slot.time === this.selectedTime)) this.selectedTime = ""
        this.phase = data.rule && data.rule.canChange ? "ready" : "blocked"
        // 목록에 있던 시간과 서버의 지금 시간이 다르면(다른 기기에서 바꿨거나 응답만 못 받은 경우) 목록을 맞춘다.
        if (this.server && timeOnly(this.server.time) !== timeOnly(this.reservation.times)) {
          this.$emit("changed", { time: timeOnly(this.server.time), maker: this.server.maker })
        }
        // 횟수를 다 쓴 예약이면 예약 상세도 '시간 변경' 단추 대신 안내를 보이게 알린다.
        if (data.rule && data.rule.reason === "LIMIT_REACHED") this.$emit("limit")
      } catch (error) {
        this.phase = "failed"
      }
    },

    async submit() {
      if (!this.selectedTime || this.submitting) return
      const wanted = this.selectedTime
      this.submitting = true
      this.errorMessage = ""
      try {
        const { data } = await this.$http.post(
          `/api/booking/reservations/${this.reservation.reservation_id}/time`,
          { time: wanted, staffUserId: this.selectedStaffId },
          { timeout: REQUEST_TIMEOUT_MS }
        )
        this.showDone(data)
      } catch (error) {
        const response = error && error.response
        const code = String((response && response.data && response.data.code) || "")
        // 서버 판단을 다시 받는다. 응답만 못 받았고 서버는 이미 바꿨으면 바꾼 것으로 보여 주고,
        // 그사이 빈 시간이나 규칙이 달라졌으면 목록·까닭을 고쳐 보여 준다.
        await this.load()
        if (this.server && timeOnly(this.server.time) === wanted) {
          this.showDone({ time: wanted, maker: this.server.maker, usedChanges: this.rule && this.rule.usedChanges, maxChanges: this.rule && this.rule.maxChanges })
        } else if (this.phase === "ready") {
          this.errorMessage = this.$t(`page.mypage.reserv.time-change.${SLOT_TAKEN_CODES.includes(code) ? "error-taken" : "error-failed"}`)
        }
      } finally {
        this.submitting = false
      }
    },

    showDone(data) {
      this.result = data
      this.phase = "done"
      this.$emit("changed", data)
      this.$nextTick(() => this.$refs.title && this.$refs.title.focus())
    },

    // 창 바깥(어두운 바탕)을 누르면 닫는다. 바탕 클릭은 dialog 요소 자신이 받는다.
    onDialogClick(event) {
      if (this.useNativeDialog && event.target === this.$refs.dialog) this.close()
    },

    // Esc는 이 창만 닫는다. 그대로 두면 뒤에 있는 예약 상세 창(v-dialog)까지 함께 닫힌다.
    onEscape(event) {
      if (this.useNativeDialog) event.stopPropagation()
    },

    close() {
      if (this.closing || this.finished || this.submitting) return
      const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches
      if (!this.useNativeDialog || reduce) return this.finish()
      this.closing = true
      window.setTimeout(this.finish, CLOSE_MS)
    },

    // 닫기 마무리. 브라우저가 창을 먼저 닫은 경우(dialog의 close 이벤트)에도 여기로 온다.
    finish() {
      if (this.finished) return
      this.finished = true
      if (this.useNativeDialog && this.$refs.dialog && this.$refs.dialog.open) this.$refs.dialog.close()
      this.$emit("close")
    },
  },
}
</script>

<style scoped>
/* 색·모서리·글자 크기는 마이페이지 예약 상세(ReservationList.vue .reservation-detail)에서 쓰는 값이다.
   라피아 웹 화면의 정해진 색(크림 바탕·로즈 계열, 브랜드기준 brand_colors.md) 한 가지만 쓴다. 검정·회색 단추를 쓰지 않는다.
   줄 높이는 모두 정수 px로 준다. 소수가 섞이면 칸 위치가 소수점에 걸려, 누른 칸만 1px 어긋나게 다시 칠해진다. */
.time-change {
  --tc-paper: #fffdf9;
  --tc-paper-clear: rgba(255, 253, 249, 0);
  --tc-band: #fff8ec;
  --tc-line: rgba(190, 160, 104, 0.32);
  --tc-line-soft: rgba(190, 160, 104, 0.17);
  --tc-line-strong: rgba(190, 160, 104, 0.48);
  --tc-title: #684945;
  --tc-text: #4f4542;
  --tc-muted: #705552;
  --tc-strong: #5e4440;
  --tc-icon: #8d7a75;
  --tc-note-bg: #f8f0e4;
  --tc-accent: #c64064;
  --tc-accent-press: #c13459;
  --tc-accent-soft: #fffaf2;
  --tc-off-bg: #f4efec;
  --tc-off-text: #a39a97;
  --tc-skeleton: #f4efec;
  --tc-outline: rgba(190, 160, 104, 0.48);
  --tc-outline-text: #705552;
  --tc-more: rgba(79, 69, 66, 0.16);

  width: 100%;
  max-width: 100%;
  max-height: 100vh;
  max-height: 100dvh;
  margin: auto 0 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--tc-text);
  overflow: visible;
  word-break: keep-all;
}


/* 일본어는 낱말 사이에 빈칸이 없어 keep-all이면 줄이 바뀌지 않고 창 밖으로 넘친다.
   되는 브라우저에서는 말 마디에서 줄을 바꾼다(auto-phrase). */
.time-change:lang(ja) {
  word-break: normal;
  line-break: strict;
}

@supports (word-break: auto-phrase) {
  .time-change:lang(ja) {
    word-break: auto-phrase;
  }
}

.time-change::backdrop {
  background: rgba(46, 30, 28, 0.5);
}

/* 휴대폰 시트는 어두운 바탕 위에 아래로 붙어 있어 그림자를 두지 않는다(보이지 않고, 사파리에서 다시 칠하는 일만 느려진다). */
.time-change__panel {
  display: flex;
  flex-direction: column;
  max-height: 100vh;
  max-height: calc(100dvh - env(safe-area-inset-top, 0px));
  padding-bottom: env(safe-area-inset-bottom);
  overflow: hidden;
  border-radius: 18px 18px 0 0;
  background: var(--tc-paper);
}

/* 열릴 때: 아래에서 올라온다. 닫힐 때는 같은 길로 더 빨리 내려간다. */
.time-change[open] .time-change__panel {
  animation: time-change-sheet-in 300ms cubic-bezier(0.32, 0.72, 0, 1);
}

.time-change[open]::backdrop {
  animation: time-change-fade-in 200ms ease-out;
}

.time-change--closing .time-change__panel {
  transform: translateY(100%);
  transition: transform 160ms cubic-bezier(0.32, 0.72, 0, 1);
}

.time-change--closing::backdrop {
  opacity: 0;
  transition: opacity 160ms ease-out;
}

@keyframes time-change-sheet-in {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

@keyframes time-change-dialog-in {
  from {
    opacity: 0;
    transform: scale(0.96);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes time-change-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.time-change__header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 44px;
  align-items: center;
  gap: 8px;
  padding: 14px 12px 12px 24px;
  border-bottom: 1px solid var(--tc-line);
  border-radius: 18px 18px 0 0;
  background: var(--tc-band);
}

.time-change__title {
  margin: 0;
  color: var(--tc-title);
  font-size: 26px;
  font-weight: 400;
  line-height: 30px;
  outline: none;
}

.time-change__close {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  transition: background-color 160ms ease-out, transform 160ms ease-out;
}

.time-change__close .v-icon {
  color: var(--tc-icon);
}

.time-change__close:active {
  transform: scale(0.94);
}

/* 가운데만 굴린다. 가려진 글이 더 있으면 위아래 끝에 옅은 그늘이 생긴다. */
.time-change__body {
  min-height: 0;
  padding: 6px 24px 16px;
  overflow: auto;
  overscroll-behavior: contain;
  background: linear-gradient(var(--tc-paper) 35%, var(--tc-paper-clear)) top / 100% 28px no-repeat local,
    linear-gradient(var(--tc-paper-clear), var(--tc-paper) 65%) bottom / 100% 28px no-repeat local,
    linear-gradient(var(--tc-more), var(--tc-paper-clear)) top / 100% 9px no-repeat scroll,
    linear-gradient(var(--tc-paper-clear), var(--tc-more)) bottom / 100% 9px no-repeat scroll;
}

.time-change__facts {
  display: grid;
  margin: 0 0 18px;
}

.time-change__facts > div {
  display: grid;
  grid-template-columns: minmax(120px, 0.38fr) minmax(0, 1fr);
  gap: 15px;
  padding: 10px 0;
  border-bottom: 1px solid var(--tc-line-soft);
}

.time-change__facts dt,
.time-change__facts dd {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
}

.time-change__facts dt {
  color: var(--tc-muted);
}

.time-change__facts dd {
  color: var(--tc-text);
  font-weight: 600;
}

.time-change__group {
  min-width: 0;
  margin: 0 0 18px;
  padding: 0;
  border: 0;
}

.time-change__label {
  margin: 0 0 4px;
  padding: 0;
  color: var(--tc-strong);
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
}

/* 무료·기한·횟수 한 줄: 시간을 고르기 전에 읽히게 제목 바로 아래에 둔다. */
.time-change__rule {
  margin: 0 0 12px;
  color: var(--tc-muted);
  font-size: 13px;
  line-height: 20px;
}

/* 한 문장씩 줄을 나누고, 문장 안에서는 마지막 낱말만 떨어지지 않게 고르게 나눈다(한 / 번이에요, 4일 / 전까지) */
.time-change__rule span {
  display: block;
  text-wrap: balance;
}

.time-change__chips {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}

.time-change__rule--space {
  visibility: hidden;
}

.time-change__chip,
.time-change__maker {
  position: relative;
  display: grid;
  place-items: center;
  min-height: 44px;
  border: 1px solid var(--tc-line-strong);
  border-radius: 9px;
  background: #fff;
  color: var(--tc-text);
  font-size: 15px;
  font-weight: 600;
  line-height: 18px;
  cursor: pointer;
  user-select: none;
  transition: background-color 160ms ease-out, border-color 160ms ease-out, color 160ms ease-out, transform 160ms ease-out;
}

.time-change__chip:active,
.time-change__maker:active {
  transform: scale(0.97);
}

.time-change__chip.is-selected,
.time-change__maker.is-selected {
  border-color: var(--tc-accent);
  background: var(--tc-accent);
  color: #fff;
}

.time-change__chip--skeleton {
  border-color: transparent;
  background: var(--tc-skeleton);
  cursor: default;
  animation: time-change-pulse 1.2s ease-in-out infinite;
}

@keyframes time-change-pulse {
  50% {
    opacity: 0.55;
  }
}

/* 고르는 동작은 숨긴 라디오가 맡는다(방향키 이동·읽어 주기는 브라우저 기본 그대로). */
.time-change__radio {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  opacity: 0;
  cursor: pointer;
}

.time-change__radio:focus-visible + span {
  outline: 2px solid var(--tc-title);
  outline-offset: 6px;
  border-radius: 4px;
}

/* 고른 칸은 바탕이 짙어, 초점 테두리를 흰색으로 그려야 보인다. */
.is-selected > .time-change__radio:focus-visible + span {
  outline-color: #fff;
}

/* 메이커 칸: 맨 위에 지금 담당 메이커, 그 아래에 겹쳐 둔 모양 가운데 가장 큰 것의 높이를 늘 차지한다. */
.time-change__maker-area {
  padding-top: 14px;
  border-top: 1px solid var(--tc-line-soft);
}

/* 지금 담당 메이커 줄: 위의 '방문 날짜·지금 예약 시간' 줄과 같은 두 칸에 맞춘다. */
.time-change__maker-area .time-change__maker-now {
  display: grid;
  grid-template-columns: minmax(120px, 0.38fr) minmax(0, 1fr);
  gap: 15px;
  margin-bottom: 10px;
  line-height: 20px;
}

/* 고른 시간의 메이커 안내가 들어오는 상자. 높이가 늘 같아, 비어 보이는 자리도 '여기에 안내가 나온다'로 읽힌다. */
.time-change__maker-stack {
  display: grid;
  padding: 12px 14px;
  border-radius: 9px;
  background: var(--tc-note-bg);
}

.time-change__maker-state {
  grid-area: 1 / 1;
  min-width: 0;
}

.time-change__maker-state.is-sizer {
  visibility: hidden;
  pointer-events: none;
}

/* 고르기 전 안내는 상자 가운데에 둔다: 글을 써넣는 칸이 아니라 '여기에 안내가 나온다'로 읽히게. */
.time-change__maker-state.is-hint {
  display: grid;
  align-content: center;
  text-align: center;
}

/* 불러오는 동안의 메이커 상자: 불러온 뒤와 비슷한 높이를 잡아 둔다. */
.time-change__maker-stack--loading {
  min-height: 160px;
}

.time-change__maker-state > :last-child {
  margin-bottom: 0;
}

.time-change__maker-hint {
  margin: 0;
  color: var(--tc-muted);
  font-size: 13px;
  line-height: 20px;
}

.time-change__group--makers .time-change__label {
  margin-bottom: 10px;
}

.time-change__makers {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.time-change__maker {
  padding: 6px 10px;
  font-size: 14px;
  text-align: center;
}

/* 지금 담당 메이커 표시: 그대로 갈 수 있다는 것을 알려 준다. */
.time-change__maker-tag {
  display: block;
  margin-top: 2px;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
}

.time-change__maker-fixed {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
  margin: 0 0 18px;
  color: var(--tc-muted);
  font-size: 14px;
  line-height: 22px;
}

.time-change__maker-fixed strong {
  color: var(--tc-text);
  font-weight: 600;
}

.time-change__maker-note {
  margin: 0 0 12px;
  color: var(--tc-strong);
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
}

.time-change__message {
  margin: 0;
  padding: 26px 4px 18px;
  color: var(--tc-strong);
  font-size: 15px;
  line-height: 26px;
  text-align: center;
  text-wrap: balance;
}

.time-change__message p {
  margin: 0 0 16px;
  text-wrap: balance;
}

.time-change__message span {
  display: block;
}

.time-change__footer {
  display: grid;
  gap: 10px;
  padding: 12px 24px 18px;
  border-top: 1px solid var(--tc-line-soft);
  background: var(--tc-paper);
}

/* 고른 시간(또는 오류)과 남은 횟수를 한 줄에 둔다. 좁으면 남은 횟수가 아랫줄로 내려간다. */
.time-change__status {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 2px 12px;
  min-height: 24px;
}

.time-change__summary,
.time-change__error {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  margin: 0;
  font-size: 14px;
  line-height: 22px;
}

.time-change__summary {
  color: var(--tc-muted);
}

.time-change__summary strong {
  color: var(--tc-accent);
  font-size: 17px;
  font-weight: 700;
}

.time-change__summary .v-icon {
  color: var(--tc-icon);
}

.time-change__error {
  align-items: flex-start;
  color: #a53a66;
}

.time-change__error .v-icon {
  margin-top: 2px;
  color: inherit;
}

.time-change__left {
  margin: 0;
  color: var(--tc-strong);
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
}

/* 메이커샷: 고른 방문 시간에 맞춰 바뀌는 촬영 시간 */
.time-change__shoot {
  display: flex;
  flex-wrap: wrap;
  gap: 0 10px;
  margin: 0;
  color: var(--tc-muted);
  font-size: 13px;
  line-height: 20px;
}

.time-change__shoot strong {
  color: var(--tc-text);
  font-weight: 600;
}

/* 아직 보일 값이 없을 때: 자리만 잡는다 */
.time-change__left.is-waiting,
.time-change__shoot.is-waiting {
  visibility: hidden;
}

.time-change__primary,
.time-change__secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 48px;
  padding: 0 20px;
  border-radius: 7px;
  font-size: 15px;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: -0.01em;
  text-decoration: none;
  transition: background-color 160ms ease-out, transform 160ms ease-out;
}

.time-change__primary {
  width: 100%;
  background: var(--tc-accent);
  color: #fff;
}

.time-change__primary:disabled {
  background: var(--tc-off-bg);
  color: var(--tc-off-text);
  cursor: default;
}

.time-change__primary.is-busy {
  cursor: progress;
}

.time-change__secondary {
  border: 1px solid var(--tc-outline);
  background: #fff;
  color: var(--tc-outline-text);
}

/* 인스타그램 DM으로 가는 단추: 닫기보다 먼저 눈에 들어오게 포인트 색 테두리를 쓴다. */
.time-change__dm {
  border-color: var(--tc-accent);
  color: var(--tc-accent);
}

.time-change__dm .v-icon {
  color: inherit;
}

.time-change__footer .time-change__secondary {
  width: 100%;
}

.time-change__primary:not(:disabled):not(.is-busy):active,
.time-change__secondary:active {
  transform: scale(0.97);
}

.time-change__primary:focus-visible,
.time-change__secondary:focus-visible,
.time-change__close:focus-visible {
  outline: 2px solid var(--tc-title);
  outline-offset: 2px;
}

.time-change__spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-top-color: #fff;
  border-radius: 50%;
  animation: time-change-spin 0.6s linear infinite;
}

@keyframes time-change-spin {
  to {
    transform: rotate(360deg);
  }
}

.time-change__done {
  display: grid;
  justify-items: center;
  gap: 6px;
  padding: 26px 24px 20px;
  text-align: center;
}

.time-change__castle {
  width: 132px;
  height: auto;
  margin-bottom: 6px;
}

.time-change__done-title {
  margin: 0;
  color: var(--tc-title);
  font-size: 18px;
  font-weight: 700;
  line-height: 26px;
}

.time-change__done-when {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: center;
  gap: 4px 10px;
  margin: 2px 0 8px;
  color: var(--tc-text);
  font-size: 15px;
  line-height: 22px;
}

.time-change__done-when strong {
  color: var(--tc-accent);
  font-size: 30px;
  font-weight: 700;
  line-height: 36px;
  letter-spacing: -0.02em;
}

.time-change__done .time-change__maker-fixed {
  justify-content: center;
  margin: 0;
}

.time-change__done-note {
  margin: 0;
  color: var(--tc-muted);
  font-size: 13px;
  line-height: 20px;
  text-wrap: balance;
}

/* 한 문장씩 줄을 나눈다(안내 칸과 같은 방식) */
.time-change__done-limit span {
  display: block;
}

.time-change__done .time-change__primary {
  margin-top: 16px;
}

.time-change__sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

/* dialog를 못 쓰는 옛 브라우저: 같은 내용을 예약 상세 안에 그대로 펼친다. */
.time-change--inline {
  margin: 16px 24px 0;
  width: auto;
  border: 1px solid var(--tc-line);
  border-radius: 18px;
}

.time-change--inline .time-change__panel {
  max-height: none;
  padding-bottom: 0;
  border-radius: 18px;
}

@media (hover: hover) and (pointer: fine) {
  .time-change__chip:not(.is-selected):not(.time-change__chip--skeleton):hover,
  .time-change__maker:not(.is-selected):hover {
    border-color: var(--tc-accent);
    background: var(--tc-accent-soft);
  }

  .time-change__close:hover {
    background: rgba(0, 0, 0, 0.06);
  }

  .time-change__primary:not(:disabled):not(.is-busy):hover {
    background: var(--tc-accent-press);
  }

  .time-change__secondary:hover {
    background: var(--tc-accent-soft);
  }
}

/* 넓은 화면: 위쪽에 놓이는 창. 가운데에 두면 내용 높이가 바뀔 때마다 머리와 시간 칸이 함께 움직인다.
   위 여백을 정수 px로 두어 칸 위치가 소수점에 걸리지 않게 한다. */
@media (min-width: 600px) {
  .time-change:not(.time-change--inline) {
    width: min(480px, calc(100% - 48px));
    margin: 24px auto auto;
  }

  .time-change__panel {
    max-height: min(680px, calc(100dvh - 48px));
    padding-bottom: 0;
    border-radius: 18px;
    box-shadow: 0 2.8px 2.2px rgba(0, 0, 0, 0.034), 0 6.7px 5.3px rgba(0, 0, 0, 0.048), 0 12.5px 10px rgba(0, 0, 0, 0.06), 0 22.3px 17.9px rgba(0, 0, 0, 0.072), 0 41.8px 33.4px rgba(0, 0, 0, 0.086), 0 100px 80px rgba(0, 0, 0, 0.12);
  }

  .time-change--inline .time-change__panel {
    box-shadow: none;
  }

  .time-change[open] .time-change__panel {
    animation: time-change-dialog-in 200ms cubic-bezier(0.23, 1, 0.32, 1);
  }

  .time-change--closing .time-change__panel {
    opacity: 0;
    transform: scale(0.97);
    transition: opacity 140ms ease-out, transform 140ms ease-out;
  }

  .time-change__chips {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }
}

@media (min-width: 600px) and (min-height: 760px) {
  .time-change:not(.time-change--inline) {
    margin-top: 64px;
  }
}

@media (min-width: 600px) and (min-height: 900px) {
  .time-change:not(.time-change--inline) {
    margin-top: 104px;
  }
}

@media (min-width: 600px) and (min-height: 1040px) {
  .time-change:not(.time-change--inline) {
    margin-top: 160px;
  }
}

/* 가로로 눕힌 휴대폰처럼 낮은 화면: 머리와 단추를 줄이고, 지금 예약 세 줄은 이름 없이 한 줄로 모아
   고를 시간 칸이 첫 화면에 보이게 한다. */
@media (max-height: 480px) {
  .time-change__header {
    padding-top: 8px;
    padding-bottom: 8px;
  }

  .time-change__title {
    font-size: 22px;
    line-height: 26px;
  }

  .time-change__facts {
    display: flex;
    flex-wrap: wrap;
    gap: 0 14px;
    margin-bottom: 8px;
  }

  .time-change__facts > div {
    display: block;
    padding: 6px 0;
    border-bottom: 0;
  }

  .time-change__facts dt {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }

  .time-change__footer {
    padding-top: 8px;
    padding-bottom: 10px;
  }

  /* 가로 화면은 넓고 낮아 규칙 글을 한 줄로 이어 시간 칸이 첫 화면에 들어오게 한다 */
  .time-change__rule span {
    display: inline;
  }

  .time-change__rule span + span::before {
    content: " ";
  }
}

@media (prefers-reduced-motion: reduce) {
  .time-change[open] .time-change__panel,
  .time-change[open]::backdrop,
  .time-change__chip--skeleton {
    animation: none;
  }

  .time-change__chip,
  .time-change__maker,
  .time-change__primary,
  .time-change__secondary,
  .time-change__close {
    transition: none;
  }
}

@media (prefers-contrast: more) {
  .time-change__panel {
    border: 2px solid var(--tc-text);
  }

  .time-change__chip,
  .time-change__maker {
    border-color: var(--tc-text);
  }
}
</style>
